import {
  CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2,
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
  CHECKOUT_ITEM_TYPE_CAR_HIRE,
  CHECKOUT_ITEM_TYPE_CRUISE,
  CHECKOUT_ITEM_TYPE_EXPERIENCE,
  CHECKOUT_ITEM_TYPE_FLIGHT,
  CHECKOUT_ITEM_TYPE_GIFT_CARD,
  CHECKOUT_ITEM_TYPE_INSURANCE,
  CHECKOUT_ITEM_TYPE_LE_HOTEL,
  CHECKOUT_ITEM_TYPE_LOYALTY_PURCHASE_POINTS,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION,
  CHECKOUT_ITEM_TYPE_PURCHASE_CREDIT,
  CHECKOUT_ITEM_TYPE_TOUR_V1,
  CHECKOUT_ITEM_TYPE_TOUR_V2,
  CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE,
  CHECKOUT_ITEM_TYPE_TRANSFER,
  CHECKOUT_ITEM_TYPE_VILLA,
  POST_PURCHASE_CHECKOUT_MODES,
} from 'constants/checkout'
import { RESERVATION_TYPE_BOOK_LATER, RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import {
  INSURANCE_TYPE_PASSPORT_CARD,
  PRODUCT_ID_NO_PROTECTION,
  PRODUCT_ID_PASSPORT_CARD,
} from 'constants/insurance'
import { hasRoomTypePackageUpgrade } from 'lib/tours/tourUtils'
import { NO_BOOKING_PROTECTION_QUOTE_ID } from 'constants/bookingProtection'
import { PAYMENT_CHECKOUT_TYPES } from 'constants/payment'
import { min } from 'lib/array/arrayUtils'
import type { IntlShape } from 'react-intl'

const accommodationItemTypes = new Set([
  CHECKOUT_ITEM_TYPE_LE_HOTEL,
  CHECKOUT_ITEM_TYPE_TOUR_V1,
  CHECKOUT_ITEM_TYPE_TOUR_V2,
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2,
  CHECKOUT_ITEM_TYPE_CRUISE,
  CHECKOUT_ITEM_TYPE_VILLA,
])

export function isInsuranceItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_INSURANCE
}

export function isInsuranceBookingProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceItem(item) && item.insuranceType === 'cfmr'
}

export function isInsuranceProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceItem(item) && item.productId !== PRODUCT_ID_NO_PROTECTION
}

export function isNoProtectionInsuranceItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceItem(item) && item.productId === PRODUCT_ID_NO_PROTECTION
}

export function isNoProtectionInsuranceBookingProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceBookingProtectionItem(item) && item.productId === PRODUCT_ID_NO_PROTECTION
}

export function isPassportCardInsuranceItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  if (!isInsuranceItem(item)) return false

  return (
    item.insuranceType === INSURANCE_TYPE_PASSPORT_CARD ||
    item.productId === PRODUCT_ID_PASSPORT_CARD ||
    item.passportCardProposalId !== undefined ||
    !!item.passportCardOptionalCovers?.length ||
    !!item.passportCardOptionalBenefitCodes?.length ||
    !!item.passportCardTravellers?.length
  )
}

export function isTravelInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return item.insuranceType !== 'cfmr' && !isPassportCardInsuranceItem(item)
}

export function isTravelInsuranceCheckoutItem(item: App.Checkout.AnyItem): item is App.Checkout.InsuranceItem {
  return isInsuranceItem(item) && isTravelInsuranceItem(item)
}

export function isXcoverInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return !isPassportCardInsuranceItem(item)
}

export function isNoProtectionXCoverInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return item.productId === PRODUCT_ID_NO_PROTECTION && isXcoverInsuranceItem(item)
}

export function isNoProtectionPassportCardInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return item.productId === PRODUCT_ID_NO_PROTECTION && item.insuranceType === INSURANCE_TYPE_PASSPORT_CARD
}

export function isAccommodationItem(item: App.Checkout.AnyItem): item is App.Checkout.AccommodationItem {
  return accommodationItemTypes.has(item.itemType)
}

export function isBundleAndSaveItem(item: App.Checkout.AnyItem): item is App.Checkout.BundleAndSaveItem {
  return item.itemType === 'bundleAndSave'
}

export function isLEHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.LEHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL
}

export function isInstantBookingLEHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.InstantBookingLEHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL && item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING &&
    !!item.checkIn && !!item.checkOut
}

export function isInstantLEHotelPostPurchaseItem(item: App.Checkout.AnyItem): item is App.Checkout.InstantBookingLEHotelPostPurchaseItem {
  return isInstantBookingLEHotelItem(item) && !!item.orderId && !!item.orderItemId
}

export function isBNBLLEHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.BNBLLEHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL && item.reservationType === RESERVATION_TYPE_BOOK_LATER
}

export function isBNBLHotelSetDatesItem(item: App.Checkout.AnyItem): item is App.Checkout.BNBLLEHotelSetDatesItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL && item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING &&
    !!item.orderId && !!item.orderItemId
}

export function isInstantBookingHotelItem(item: App.Checkout.AnyItem): item is App.Checkout.InstantBookingItem {
  return !isBNBLLEHotelItem(item)
}

export function isBedbankItem(item: App.Checkout.AnyItem): item is App.Checkout.BedbankHotelItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_BEDBANK
}

export function isAccommodationV2Item(item: App.Checkout.AnyItem): item is App.Checkout.AccommodationV2Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2
}

export function isMerchandiseItem(item: App.Checkout.AnyItem): item is App.Checkout.MerchandiseItem {
  return item.itemType === 'merchandise'
}

export function isLoyaltyPurchasePointsItem(item: App.Checkout.AnyItem): item is App.Checkout.LoyaltyPurchasePointsItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LOYALTY_PURCHASE_POINTS
}

export function hasOnlyLoyaltyPurchasePointsItems(items: Array<App.Checkout.AnyItem>): boolean {
  if (items.length === 0) return false
  return items.every(isLoyaltyPurchasePointsItem)
}

export function isMerchandiseShippingItem(item: App.Checkout.AnyItem): item is App.Checkout.MerchandiseShippingItem {
  return item.itemType === 'merchandise-shipping'
}

export function hasOnlyMerchandiseItems(items: Array<App.Checkout.AnyItem>): boolean {
  if (items.length === 0) return false
  return items.every(item => isMerchandiseItem(item) || isMerchandiseShippingItem(item))
}

export function isTourV1Item(item: App.Checkout.AnyItem): item is App.Checkout.LETourV1Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1
}

export function isTourV2Item(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2
}

export function isTourV2ExperienceItem(item: App.Checkout.AnyItem): item is App.Checkout.TourV2ExperienceItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE
}

export function isTourItem(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item | App.Checkout.LETourV1Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2 || item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1
}

export function isTourV2ItemWithPackageUpgrade(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2 && (!item.purchasableOption.roomType || hasRoomTypePackageUpgrade(item.purchasableOption.roomType))
}

export function isTourV2ItemWithoutRoom(item: App.Checkout.AnyItem): item is App.Checkout.TourV2Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2 && !item.purchasableOption.roomType
}

export function isCruiseItem(item: App.Checkout.AnyItem): item is App.Checkout.CruiseItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_CRUISE
}

export function isCarHireItem(item: App.Checkout.AnyItem): item is App.Checkout.CarHireItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_CAR_HIRE
}

export function isLEAccommodationItem(item: App.Checkout.AnyItem): item is App.Checkout.LEHotelItem | App.Checkout.LETourV1Item {
  return item.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL || item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1
}

export function isExperienceItem(item: App.Checkout.AnyItem): item is App.Checkout.ExperienceItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_EXPERIENCE
}

export function isBundledExperienceItem(item: App.Checkout.AnyItem): item is App.Checkout.TransferItem | App.Checkout.ExperienceItem {
  const isAccomBundle = (isExperienceItem(item) || isTransferItem(item)) && !!item.isAccomBundle
  const isPassport = isExperienceItem(item) && !!item.accommodationParentItemId

  return isAccomBundle || isPassport
}

interface LiveAccomIndex {
  accommItemAndBundleIds: Set<string>
}

function indexLiveAccommodation(items: Array<App.Checkout.AnyItem>): LiveAccomIndex {
  const accommItemAndBundleIds = new Set<string>()
  for (const item of items) {
    if (!item || !isAccommodationItem(item)) continue
    if (!('accommBundleIds' in item && item.accommBundleIds?.length)) continue
    for (const accommBundleId of item.accommBundleIds) {
      accommItemAndBundleIds.add(`${item.itemId}:${accommBundleId}`)
    }
  }
  return { accommItemAndBundleIds }
}

function isOrphanedAgainstLiveAccommodation(
  item: App.Checkout.AnyItem,
  { accommItemAndBundleIds }: LiveAccomIndex,
): boolean {
  // Filter out irrelevant items (including Passport experience items).
  if (!item || !isBundledExperienceItem(item)) return false
  const accommBundleId = item.isAccomBundle
  if (!accommBundleId) return false
  // If the bundled experience/transfer item doesn't point to any parent accommodation items,
  // something's wrong and we should throw it out.
  if (!item.bundledItemIds?.length) return true

  // If any parent accommodation items are missing, treat this experience/transfer item as orphaned.
  for (const accommItemId of item.bundledItemIds) {
    if (!accommItemAndBundleIds.has(`${accommItemId}:${accommBundleId}`)) return true
  }
  return false
}

export function isOrphanedBundledExperienceItem(
  item: App.Checkout.AnyItem,
  items: Array<App.Checkout.AnyItem>,
): boolean {
  return isOrphanedAgainstLiveAccommodation(item, indexLiveAccommodation(items))
}

function getIncompleteBundleItemIds(
  items: Array<App.Checkout.AnyItem>,
): Set<string> {
  // We optionally chain to `itemId` to tolerate malformed `item`s.
  const itemIds = new Set<string>(items.flatMap(item => item?.itemId ? [item.itemId] : []))
  const incompleteBundleItemIds = new Set<string>()
  const index = indexLiveAccommodation(items)
  // With a bundle group, parent accommodation items will have the same set of `accommBundleIds` and
  // child experience/transfer items will have the same set of `bundledItemIds`. Thus, a single pass
  // is sufficient to identify candidates to prune.
  for (const item of items) {
    if (!isOrphanedAgainstLiveAccommodation(item, index)) continue
    // This item is an orphaned child experience/transfer item. Condemn it and its surviving
    // parents.
    const childItem = item as App.Checkout.ExperienceItem | App.Checkout.TransferItem
    incompleteBundleItemIds.add(childItem.itemId)
    if (!childItem.bundledItemIds) continue
    for (const parentItemId of childItem.bundledItemIds) {
      if (!itemIds.has(parentItemId)) continue
      incompleteBundleItemIds.add(parentItemId)
    }
  }
  return incompleteBundleItemIds
}

export function pruneIncompleteBundleItems(
  items: Array<App.Checkout.AnyItem>,
): Array<App.Checkout.AnyItem> {
  const incompleteBundleItemIds = getIncompleteBundleItemIds(items)
  // We optionally chain to `itemId` to tolerate malformed `item`s.
  return items.filter(item => !incompleteBundleItemIds.has(item?.itemId))
}

export function getEffectivelyIncompleteBundleItemIds(
  removedItemId: string,
  items: Array<App.Checkout.AnyItem>,
): Set<string> {
  // We optionally chain to `itemId` to tolerate malformed `item`s.
  const remaining = items.filter(item => item?.itemId !== removedItemId)
  return getIncompleteBundleItemIds(remaining)
}

export function isTransferItem(item: App.Checkout.AnyItem): item is App.Checkout.TransferItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_TRANSFER
}

export function isFlightItem(item: App.Checkout.AnyItem): item is App.Checkout.FlightItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_FLIGHT
}

export function isGiftCardItem(item: App.Checkout.AnyItem): item is App.Checkout.GiftCardItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_GIFT_CARD
}

export function isPurchaseCreditItem(item: App.Checkout.AnyItem): item is App.Checkout.PurchaseCreditItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_PURCHASE_CREDIT
}

export function isLuxPlusSubscriptionItem(item: App.Checkout.AnyItem): item is App.Checkout.LuxPlusSubscriptionItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION
}

export function isSubscriptionJoinItem(item: App.Checkout.AnyItem): item is App.Checkout.SubscriptionJoinItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE
}

function formatContactNames(contactNames: string | Array<string>, intl: IntlShape) {
  const names = (Array.isArray(contactNames) ? contactNames : [contactNames]).filter(Boolean)
  if (names.length <= 1) return names[0] ?? ''

  return intl.formatList(names, { type: 'conjunction' })
}

export function getConnectRoomsSpecialRequestMessage(contactNames: string | Array<string>, intl: IntlShape): string {
  const formattedContactNames = formatContactNames(contactNames, intl)

  if (Array.isArray(contactNames) && contactNames.length > 1) {
    return intl.formatMessage(
      {
        id: 'k4zTivJVB_bpILhzywy5',
        defaultMessage: 'All rooms under the names {contactNames}, are requested to be connecting rooms or within close proximity.',
        description: 'Special request message asking for connecting or nearby rooms under multiple booking names',
      },
      { contactNames: formattedContactNames },
    )
  }

  return intl.formatMessage(
    {
      id: 'wMcq6F7OEeHRzbMZwbU8',
      defaultMessage: 'All rooms under the name {contactName}, are requested to be connecting rooms or within close proximity.',
      description: 'Special request message asking for connecting or nearby rooms under one booking name',
    },
    { contactName: formattedContactNames },
  )
}

export function getTourV2IdForCheckoutItem(item: App.Checkout.TourV2Item): string {
  return item.privateRequestKey ? `${item.offerId}-${item.privateRequestKey}` : item.offerId
}

export function isVillaItem(item: App.Checkout.AnyItem): item is App.Checkout.VillaItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_VILLA
}

export function isBookingProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.BookingProtectionItem {
  return item.itemType === CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION
}

export function isBookingProtectionWithProtectionItem(item: App.Checkout.AnyItem): item is App.Checkout.BookingProtectionItem {
  return isBookingProtectionItem(item) && item.quoteId !== NO_BOOKING_PROTECTION_QUOTE_ID
}

export function isCruiseV1(item: App.Checkout.AnyItem, leOffers: Record<string, App.Offer | undefined>): boolean {
  if (item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1) {
    const offer = leOffers[item.offerId]
    return !!offer && !!offer.holidayTypes?.includes('Cruises')
  }
  return true
}

export function getItemOfferId(item?: App.Checkout.AccommodationItem) {
  if (!!item && isCruiseItem(item)) return `cruise-details:${item.offerId}`
  return item?.offerId
}

/**
 * The supplier behind an accommodationV2 item can change after checkout entry — a
 * `fallback_selected` enquiry outcome swaps it — so the session's `selectedSource` outranks the
 * `internalService` captured on the cart item at shop time.
 */
export function getAccommodationV2ItemService(
  item: App.Checkout.AccommodationV2Item,
  accommodationSessions: App.CheckoutState['accommodationSessions'],
): 'bedbank' | 'reservation' {
  const selectedSource = item.sessionId ? accommodationSessions[item.sessionId]?.selectedSource : undefined
  return selectedSource ?? item.internalService
}

export function isAccommodationV2AccommodationItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.AccommodationV2AccommodationItemView {
  return ('kind' in itemView && itemView.kind === 'accommodationV2') ||
    (!!itemView.item && isAccommodationV2Item(itemView.item))
}

export function isAccommodationV2AccommodationOfferView(
  view: App.Checkout.AccommodationOfferView,
): view is App.Checkout.AccommodationV2AccommodationOfferView {
  // offerType is the offer-content type (LE vs bedbank) and can match a legacy hotel
  // view after fallback. Item `kind` is the discriminator.
  return view.itemViews.some(isAccommodationV2AccommodationItemView)
}

export function isBedbankAccommodationOfferView(
  view: App.Checkout.AccommodationOfferView,
): view is App.Checkout.BedbankAccommodationOfferView {
  return view.itemViews.some(isBedbankAccommodationItemView)
}

export function isBedbankAccommodationItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.BedbankAccommodationItemView {
  return ('kind' in itemView && itemView.kind === 'bedbank') ||
    (!!itemView.item && isBedbankItem(itemView.item))
}

export function isLEHotelAccommodationItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.LEAccommodationItemView {
  return isLEHotelItem(itemView.item)
}

export function isInsuranceItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.InsuranceItemView {
  return isInsuranceItem(itemView.item)
}

export function isCarHireItemView(
  itemView: App.Checkout.AnyItemView,
): itemView is App.Checkout.CarHireItemView {
  return isCarHireItem(itemView.item)
}

export function findPostPurchaseCheckout(mode?: App.CheckoutCartMode) {
  if (mode && POST_PURCHASE_CHECKOUT_MODES.has(mode)) return mode
}

export function isLoyaltyLoungePassItem(item?: App.Checkout.AnyItem): item is App.Checkout.LoyaltyLoungePassItem {
  return item?.itemType === 'loungePassItem'
}

export function isLoyaltyTransferItem(item?: App.Checkout.AnyItem): item is App.Checkout.LoyaltyTransferItem {
  return item?.itemType === 'loyaltyTransferItem'
}

export function isExperienceTicketViewItem(item?: App.Checkout.AnyItem): item is App.Checkout.ExperienceTicketViewItem {
  return item?.itemType === 'experienceTicket'
}

export function mapCartModeToCheckoutType(cartMode: App.CheckoutCartMode | undefined): App.PaymentCheckoutType {
  if (!cartMode) {
    return PAYMENT_CHECKOUT_TYPES.Unknown
  }
  switch (cartMode) {
    case 'change-dates':
      return PAYMENT_CHECKOUT_TYPES.ChangeDates
    case 'change-package':
      return PAYMENT_CHECKOUT_TYPES.ChangePackage
    case 'add-extra-guests':
      return PAYMENT_CHECKOUT_TYPES.AddExtraGuests
    case 'custom-offer':
    case 'custom-offer-v2':
      return PAYMENT_CHECKOUT_TYPES.CustomOffer
    case 'flight-pnr-import':
      return PAYMENT_CHECKOUT_TYPES.Default
    case 'instalment':
      return PAYMENT_CHECKOUT_TYPES.Instalment
    case 'instalment-balance':
      return PAYMENT_CHECKOUT_TYPES.InstalmentBalance
    case 'deposit-balance':
      return PAYMENT_CHECKOUT_TYPES.DepositBalance
    case 'reserve-balance':
      return PAYMENT_CHECKOUT_TYPES.ReserveBalance
    case 'payment-schedule-balance':
      return PAYMENT_CHECKOUT_TYPES.PaymentScheduleBalance
    case 'default':
      return PAYMENT_CHECKOUT_TYPES.Default
    default:
      return PAYMENT_CHECKOUT_TYPES.Unknown
  }
}

export function getShortestFlightExpiry(flightItems: Array<App.Checkout.FlightItem>): number | undefined {
  const shortest = min(flightItems.filter(item => item.expiry), item => item.expiry!)
  return shortest?.expiry
}
